import { useState } from 'react';
import * as React from 'react';
import { Tree } from 'antd';
import type { TreeDataNode } from 'antd';

import FormHeader from '../FormHeader';
import InfoPopover from '../../ui/InfoPopover';

interface DataSelectionProps {
    selectedDataPoint: string | null;
    setSelectedDataPoint: (dataPoint: string | null) => void;
    onVolumeOrCurrencySelected?: (isSelected: boolean) => void;
}

const infoPopoverContent = {
    adjusted: `"Adjusted" prices are stock price values that were amended to accurately reflect the given stock's value after accounting for any corporate actions, such as splits or dividends. Adjustments are made in accordance with the "CRSP Calculations" methodology set forth by the Center for Research in Security Prices (CRSP).`,
    tradingCurrency: `Returns the currency in which the selected companies share price is traded in.`,
    tradingVolume: `Returns the total number of shares traded in the selected companies shares on a given day.`,
};

const DataSelection: React.FC<DataSelectionProps> = ({ selectedDataPoint, setSelectedDataPoint, onVolumeOrCurrencySelected }) => {
    const treeData: TreeDataNode[] = [
        {
            title: 'Share Prices',
            key: 'share-prices',
            children: [
                {
                    title: 'Closing Share Price',
                    key: 'Closing_Share_Price',
                    isLeaf: true,
                },
                {
                    title: 'Opening Share Price',
                    key: 'Opening_Share_Price',
                    isLeaf: true,
                },
                {
                    title: 'Daily High',
                    key: 'Daily_High',
                    isLeaf: true,
                },
                {
                    title: 'Daily Low',
                    key: 'Daily_Low',
                    isLeaf: true,
                },
                {
                    title: <span>Adjusted Closing Share Price { }<InfoPopover content={infoPopoverContent.adjusted} trigger="hover" placement='bottom' /></span>,
                    key: 'Adjusted_Closing_Share_Price',
                    isLeaf: true,
                }
            ],
        },
        {
            title: 'Volume and Trading Currency',
            key: 'volume-and-trading-currency',
            children: [
                {
                    title: <span>Trading Volume { }<InfoPopover content={infoPopoverContent.tradingVolume} trigger="hover" placement='bottom' /></span>,
                    key: 'Traded_Volume_Shares',
                    isLeaf: true,
                },
                {
                    title: <span>Trading Currency { }<InfoPopover content={infoPopoverContent.tradingCurrency} trigger="hover" placement='bottom' /></span>,
                    key: 'Trading_Currency',
                    isLeaf: true,
                }
            ]
        }
    ];

    const onSelect = (selectedKeys: React.Key[], info: any) => {
        if (selectedKeys.length === 0) {
            setSelectedDataPoint(selectedDataPoint);
            return;
        }

        if (selectedKeys.length > 0 && info.node.isLeaf) {
            const selected = selectedKeys[0].toString();
            setSelectedDataPoint(selected);
            if (onVolumeOrCurrencySelected) {
                const isVolumeOrCurrency = ['Traded_Volume_Shares', 'Trading_Currency'].includes(selected);
                onVolumeOrCurrencySelected(isVolumeOrCurrency);
            }
        } else {
            setSelectedDataPoint(selectedDataPoint);
            if (onVolumeOrCurrencySelected) {
                const isVolumeOrCurrency = ['Traded_Volume_Shares', 'Trading_Currency'].includes(selectedDataPoint);
                onVolumeOrCurrencySelected(isVolumeOrCurrency);
            }
        }
    };

    return (
        <div>
            <FormHeader title="Data Selection" />
            <Tree
                defaultExpandAll
                selectedKeys={[selectedDataPoint]}
                onSelect={onSelect}
                treeData={treeData}
                showLine={{ showLeafIcon: false }}
            />
        </div>
    );
};

export default DataSelection; 